import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Share2, Star, Trash2, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';
import { useAudioStore } from '@/stores/audioStore';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Now-Playing 3-dots menu. 4 actions in launch scope:
 *
 *   - "Zur Story"         → navigate(`/story/${storyId}`). Dismisses
 *                            Now-Playing and opens the reading view.
 *                            Audio continues via MiniPlayer.
 *   - "Geschichte teilen" → fires `onShareClick` callback — parent
 *                            (NowPlayingPage) fetches story detail on
 *                            demand and opens the ShareBottomSheet.
 *   - "Bewerten"          → fires `onRateClick` callback — parent
 *                            navigates to /story/:id?scrollTo=rating.
 *   - "Löschen"           → still aria-disabled. Backend DELETE
 *                            endpoint is deferred to post-launch per
 *                            the 6.2.3 scope decision.
 *
 * Popover open state is controlled internally so menu-item clicks can
 * close the popover before firing their callbacks. "Zur Story" and
 * "Bewerten" trigger route changes that would unmount the menu anyway,
 * but closing explicitly keeps the affordance consistent across
 * actions.
 *
 * Gate-0 F8: the menu label stays generic "Geschichte teilen" —
 * audioStore does not carry storyType, so mode-awareness is resolved
 * inside the ShareBottomSheet once the parent has the story detail.
 */
interface NowPlayingMenuProps {
  onShareClick?: () => void;
  onRateClick?: () => void;
}

export function NowPlayingMenu({ onShareClick, onRateClick }: NowPlayingMenuProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const storyId = useAudioStore((s) => s.storyId);

  const copy = STRINGS.shell.nowPlaying;

  const close = () => setOpen(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={copy.ariaLabel.menu}
          className="flex h-10 w-10 items-center justify-center text-dream-white opacity-75 transition-opacity hover:opacity-100"
        >
          <MoreVertical className="h-6 w-6" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56">
        <MenuItem
          icon={<BookOpen className="h-4 w-4" strokeWidth={2} />}
          label={copy.menu.goToStory}
          onClick={() => {
            close();
            if (storyId) navigate(`/story/${storyId}`);
          }}
        />
        <MenuItem
          icon={<Share2 className="h-4 w-4" strokeWidth={2} />}
          label={copy.menu.share}
          onClick={() => {
            close();
            onShareClick?.();
          }}
        />
        <MenuItem
          icon={<Star className="h-4 w-4" strokeWidth={2} />}
          label={copy.menu.rate}
          onClick={() => {
            close();
            onRateClick?.();
          }}
        />
        <MenuItem
          icon={<Trash2 className="h-4 w-4" strokeWidth={2} />}
          label={copy.menu.delete}
          disabled
          disabledHint={copy.menu.disabledHint}
        />
      </PopoverContent>
    </Popover>
  );
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  disabledHint?: string;
}

function MenuItem({ icon, label, onClick, disabled, disabledHint }: MenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      title={disabled ? disabledHint : undefined}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
        !disabled && 'hover:bg-muted',
        disabled && 'cursor-default opacity-40',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
